// -----------------------------------------------------------------------------
// authLinks.ts — v0.8.0 sign-in helper screens that work while nobody is signed in (boards A0.1, A0.2).
//   #/forgot-password                                   A0.1 Forgot password (from A0 Sign in)
//   #/set-password?token_hash=…&type=invite|recovery    A0.2 Set password (the link in the invite / reset email,
//                                                       docs/email-templates/; the dev server's outbox uses it too)
// App.tsx shows these before the sign-in gate. Pure functions — unit-tested in tests/unit/authLinks.test.ts.
// -----------------------------------------------------------------------------
import type { EmailLinkType } from "./api/backend";

export type AuthScreen =
  | { name: "forgot" }
  | { name: "set-password"; tokenHash: string; type: EmailLinkType }
  | { name: "set-password"; tokenHash: null; type: null };                 // link without its token: shown as expired

/** Which sign-in helper screen the address asks for, or null for the normal app. */
export function authScreen(hash: string): AuthScreen | null {
  const h = hash.replace(/^#\/?/, "");
  const [path, query = ""] = h.split("?");
  if (path === "forgot-password") return { name: "forgot" };
  if (path !== "set-password") return null;
  const q = new URLSearchParams(query);
  const tokenHash = q.get("token_hash"), type = q.get("type");
  if (tokenHash && (type === "invite" || type === "recovery")) return { name: "set-password", tokenHash, type };
  return { name: "set-password", tokenHash: null, type: null };
}

export const PASSWORD_MIN = 8;
/** A0.2 New password rules (tooltip): at least 8 characters, letters and numbers, both boxes the same. */
export function passwordProblems(pw: string, confirm: string): string[] {
  const out: string[] = [];
  if (pw.length < PASSWORD_MIN) out.push(`At least ${PASSWORD_MIN} characters`);
  if (!/[A-Za-z]/.test(pw) || !/[0-9]/.test(pw)) out.push("Use letters and numbers");
  if (pw !== confirm) out.push("The two passwords do not match");
  return out;
}

/** First name for the welcome line ("Welcome to BuildNexx, Farah"). */
export const firstName = (full: string) => full.trim().split(/\s+/)[0] ?? "";

/** What Add member / Resend invite tells the person who clicked (toast). */
export function inviteToast(r: { email: string; sent: "invite" | "reset" | "none" }, again = false): string {
  if (r.sent === "reset") return `${r.email} already had a login — a set-password email was sent instead`;
  if (r.sent === "none") return `Added — ${r.email} already uses BuildNexx, so no invite was needed`;
  return again ? `Invitation sent again to ${r.email}` : `Invitation sent to ${r.email}`;
}

// ---- v0.9.2 (round 33 #3, Kyle 03/Nov/26): a link opened while signed out ----------------------------------------
// The address becomes #/sign-in?next=<the linked page>; after signing in the app goes on to that page.
/** Signed out on `hash`: the sign-in address to show instead, or null when it is already right (or a helper screen). */
export function signInHash(hash: string): string | null {
  if (authScreen(hash) || /^#\/sign-in(\?|$)/.test(hash)) return null;
  const path = hash.replace(/^#/, "").split("?")[0];
  return path && path !== "/" ? `#/sign-in?next=${encodeURIComponent(path)}` : "#/sign-in";
}
/** Just signed in on `hash`: where to go — the ?next page (only an app path), Projects, or null when not on #/sign-in. */
export function afterSignIn(hash: string): string | null {
  if (!/^#\/sign-in(\?|$)/.test(hash)) return null;
  const next = new URLSearchParams(hash.split("?")[1] ?? "").get("next") ?? "";
  return /^\/[A-Za-z0-9/_\-.%]*$/.test(next) && !next.startsWith("//") ? `#${next}` : "#/";
}
/** The project code a path belongs to (#/p/BIM-072/… → BIM-072), to check access after sign-in. */
export const projectOfPath = (hash: string) => /^#\/p\/([^/?]+)/.exec(hash)?.[1] ?? null;
